import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    Alert,
    Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { getAuthToken, getAudienceProfile, setAudienceProfile } from '@virtual-cinema/client-lib';
import AudienceAppBar from '../common/app-bar';
import dayjs from 'dayjs';

// US States list
const US_STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const ProfilePage = () => {
    const [profile, setProfile] = useState({
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: null,
        gender: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [dirtyFields, setDirtyFields] = useState(new Set());
    const [isEditing, setIsEditing] = useState(false);
    const accessToken = getAuthToken();

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await getAudienceProfile(accessToken);
            if (response?.data) {
                const info = response.data.info || {};
                setProfile({
                    firstName: info.firstname || '',
                    lastName: info.lastname || '',
                    email: response.data.email || '',
                    dateOfBirth: info.dateOfBirth ? dayjs(info.dateOfBirth) : null,
                    gender: info.gender || '',
                    address1: info.address1 || '',
                    address2: info.address2 || '',
                    city: info.city || '',
                    state: info.state || '',
                    zipCode: info.zipCode || '',
                });
            }
        } catch (err) {
            setError('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setProfile(prev => ({ ...prev, [field]: value }));
        setDirtyFields(prev => new Set(prev.add(field)));
        if (!isEditing) setIsEditing(true);
    };

    const handleSave = async () => {
        if (!profile.firstName.trim() || !profile.lastName.trim()) {
            setError('First name and last name are required');
            return;
        }

        try {
            const info = {
                firstname: profile.firstName.trim(),
                lastname: profile.lastName.trim(),
                dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.format('YYYY-MM-DD') : null,
                gender: profile.gender,
                address1: profile.address1.trim(),
                address2: profile.address2.trim(),
                city: profile.city.trim(),
                state: profile.state,
                zipCode: profile.zipCode.trim(),
            };

            const response = await setAudienceProfile(info, accessToken);
            if (response?.error) throw new Error(response.error);

            setSuccess('Profile updated successfully');
            setIsEditing(false);
            setDirtyFields(new Set());
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setDirtyFields(new Set());
        fetchProfile();
    };

    const SectionTitle = ({ children }) => (
        <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 500 }}>
            {children}
        </Typography>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <AudienceAppBar />
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4
            }}>
                <Typography variant="h4" sx={{ color: 'text.primary' }}>
                    Profile
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Box sx={{
                backgroundColor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#fff',
                borderRadius: 1,
                p: 3,
                boxShadow: 1
            }}>
                {/* Personal Information Section */}
                <SectionTitle>Personal Information</SectionTitle>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>First Name *</Typography>
                        <TextField
                            fullWidth
                            value={profile.firstName}
                            onChange={(e) => handleFieldChange('firstName', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                            error={dirtyFields.has('firstName') && !profile.firstName.trim()}
                            helperText={dirtyFields.has('firstName') && !profile.firstName.trim() ? 'First name is required' : ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>Last Name *</Typography>
                        <TextField
                            fullWidth
                            value={profile.lastName}
                            onChange={(e) => handleFieldChange('lastName', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                            error={dirtyFields.has('lastName') && !profile.lastName.trim()}
                            helperText={dirtyFields.has('lastName') && !profile.lastName.trim() ? 'Last name is required' : ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>Email</Typography>
                        <TextField
                            fullWidth
                            value={profile.email}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>Date of Birth</Typography>
                        <DatePicker
                            value={profile.dateOfBirth}
                            onChange={(newValue) => handleFieldChange('dateOfBirth', newValue)}
                            renderInput={(params) => <TextField {...params} fullWidth onFocus={() => !isEditing && setIsEditing(true)} />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>Gender</Typography>
                        <TextField
                            fullWidth
                            select
                            value={profile.gender}
                            onChange={(e) => handleFieldChange('gender', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                        >
                            <MenuItem value="">Not specified</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Contact Information Section */}
                <SectionTitle>Contact Information</SectionTitle>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography sx={{ mb: 1 }}>Address</Typography>
                        <TextField
                            fullWidth
                            value={profile.address1}
                            onChange={(e) => handleFieldChange('address1', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                            placeholder="Street address"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={profile.address2}
                            onChange={(e) => handleFieldChange('address2', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                            placeholder="Apt, Suite, Bldg (optional)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>City</Typography>
                        <TextField
                            fullWidth
                            value={profile.city}
                            onChange={(e) => handleFieldChange('city', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>State</Typography>
                        <TextField
                            fullWidth
                            select
                            value={profile.state}
                            onChange={(e) => handleFieldChange('state', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                        >
                            {US_STATES.map(state => (
                                <MenuItem key={state.code} value={state.code}>
                                    {state.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>ZIP Code</Typography>
                        <TextField
                            fullWidth
                            value={profile.zipCode}
                            onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                            onFocus={() => !isEditing && setIsEditing(true)}
                            inputProps={{ maxLength: 5 }}
                            placeholder="5-digit ZIP code"
                        />
                    </Grid>
                </Grid>

                {isEditing && (
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!profile.firstName.trim() || !profile.lastName.trim()}
                        >
                            Save Changes
                        </Button>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default ProfilePage;